import React, { useState } from 'react';

import { useScroll } from 'utils/hooks';
import { ACTIVE_FILTER, CLOSED_FILTER } from 'pages/CaseManager/constants';
import { NoResultMessage } from 'components/Table';
import { DotPulse } from 'components/loaders';
import { applyFilters } from 'pages/CaseManager/filters';

import {
  ResultsTableStyled,
  FiltersWrapper,
  Filter,
  FilterLabel,
  Table,
  Badge,
  NoOldCasesMessage,
} from './styledComponents';
import { ResultRow } from './ResultRow';

const ResultsTable = ({
  items = [],
  onTabSelect,
  tabFilter,
  clearAllFilters,
  allResultsCount = 0,
  loading = false,
}) => {
  const INITIAL_ITEMS = 20;
  const SCROLL_INCREMENT_ITEMS = 10;

  const [maxItems, setMaxItems] = useState(INITIAL_ITEMS);

  const scrollElement = useScroll({
    action: ({ yPercent }) => {
      const shouldDisplayMore = items.length > maxItems && yPercent > 75;

      if (shouldDisplayMore) {
        setMaxItems(maxItems + SCROLL_INCREMENT_ITEMS);
      }
    },
  });

  const currentTabItems = applyFilters({ items, tabFilter });
  const itemsToShow = currentTabItems.slice(0, maxItems);

  const remainingItemsCount = items.length - currentTabItems.length;
  const isActiveTab = tabFilter === ACTIVE_FILTER;

  const renderNoResultsMessage = () => {
    if (itemsToShow.length) {
      return null;
    } else if (allResultsCount) {
      return (
        <NoResultMessage>
          No results found using these filters. Please <span onClick={clearAllFilters}>clear</span>{' '}
          filters to see results.
        </NoResultMessage>
      );
    } else {
      return <NoResultMessage>No RMA Cases found.</NoResultMessage>;
    }
  };

  const renderEndOfListMessage = () => {
    if (itemsToShow.length && itemsToShow.length === currentTabItems.length) {
      return (
        <NoResultMessage>
          No more results. Click{' '}
          <span onClick={() => document.documentElement.scrollIntoView(true)}>here</span> to scroll
          to the top of the list
        </NoResultMessage>
      );
    } else return null;
  };

  return (
    <ResultsTableStyled>
      <FiltersWrapper>
        <Filter selected={isActiveTab} onClick={() => onTabSelect(ACTIVE_FILTER)}>
          <FilterLabel>ACTIVE RMA CASES</FilterLabel>
          <Badge>{isActiveTab ? currentTabItems.length : remainingItemsCount}</Badge>
        </Filter>
        <Filter selected={!isActiveTab} onClick={() => onTabSelect(CLOSED_FILTER)}>
          <FilterLabel>CLOSED RMA CASES</FilterLabel>
          <Badge>{!isActiveTab ? currentTabItems.length : remainingItemsCount}</Badge>
        </Filter>
      </FiltersWrapper>
      <NoOldCasesMessage>Note: only includes cases created within 2 years.</NoOldCasesMessage>
      <Table ref={scrollElement}>
        <thead>
          <tr>
            <th>Case #</th>
            <th>RMA #</th>
            <th>Contact</th>
            <th>Owner</th>
            <th>Currency</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {itemsToShow.map((item) => (
            <ResultRow key={item.id} item={item} />
          ))}
        </tbody>
      </Table>
      {loading ? (
        <DotPulse />
      ) : (
        <>
          {renderNoResultsMessage()}
          {renderEndOfListMessage()}
        </>
      )}
    </ResultsTableStyled>
  );
};

export { ResultsTable };
